import React, { useMemo, useState } from 'react';
import { X, Save, Loader2, Tag, Plus } from 'lucide-react';
import DiffView from 'features/prompts/components/DiffView';
import BrandSelect from 'features/playground/components/BrandSelect';
import { draftToLines, diffLines } from 'features/prompts/lib/promptDiff';
import { shortHash } from 'features/prompts/lib/promptMembers';

/**
 * SaveVersionModal — the shared "save a new prompt version" dialog: a GitHub-style
 * line diff of what changed, an optional commit message, and an optional label to
 * push. Used by the prompt editor and the playground so both save the same way.
 *
 * props:
 *   draft        {messages, model, variables, tools, response_format} being saved
 *   baseline     the version this diffs against (version-like, or null = new)
 *   existingName the prompt's name, or null when saving a brand-new prompt
 *   versions     the prompt's existing versions (for label-move detection)
 *   teamLabels   string[] of the team's deploy labels
 *   onCreateLabel(label) → Promise<label|null>   create a team label
 *   onSave({ name, commit_message, labels }) → Promise<version>   throws on error
 *   onClose()
 */

const labelCls = 'mb-1 block text-[11px] font-semibold uppercase tracking-wider text-[color:var(--text-muted)]';
const inputCls = 'w-full rounded-lg border border-[color:var(--border-default)] bg-[color:var(--surface-base)] px-2.5 py-1.5 text-[12.5px] text-[color:var(--text-primary)] focus:outline-none focus:border-[color:var(--border-strong)] placeholder:text-[color:var(--text-disabled)]';

export default function SaveVersionModal({
  draft, baseline, existingName = null, versions = [], teamLabels = [],
  onCreateLabel, onSave, onClose,
}) {
  const [name, setName] = useState('');
  const [commitMessage, setCommitMessage] = useState('');
  const [chosenLabel, setChosenLabel] = useState('');
  const [addingLabel, setAddingLabel] = useState(false);
  const [newLabel, setNewLabel] = useState('');
  const [confirmMove, setConfirmMove] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const isNew = !existingName;
  const baselineHash = baseline?.version_hash ? shortHash(baseline.version_hash) : null;

  const diff = useMemo(
    () => diffLines(baseline ? draftToLines(baseline) : [], draftToLines(draft)),
    [baseline, draft],
  );

  const labelHolder = chosenLabel
    ? versions.find((v) => (v.labels || []).includes(chosenLabel) && v.version_hash !== baseline?.version_hash)
    : null;
  const labelMoves = Boolean(chosenLabel && labelHolder);

  const confirmAddLabel = async () => {
    const raw = newLabel.trim();
    if (!raw) return;
    const created = await onCreateLabel?.(raw);
    if (created) { setChosenLabel(created); setAddingLabel(false); setNewLabel(''); }
  };

  const doSave = async () => {
    if (isNew && !name.trim()) { setError('Give the prompt a name.'); return; }
    setSaving(true);
    setError(null);
    try {
      await onSave({
        name: name.trim().replace(/\s+/g, '-').replace(/[^a-zA-Z0-9._-]/g, ''),
        commit_message: commitMessage.trim() || undefined,
        labels: chosenLabel ? [chosenLabel] : [],
      });
      onClose();
    } catch (e) {
      const d = e?.response?.data;
      setError((d?.details && d.details.join('; ')) || d?.error || e.message || 'Save failed');
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 p-4" onMouseDown={onClose}>
      <div className="w-[460px] max-w-[92vw] rounded-xl border border-[color:var(--border-default)] bg-[color:var(--surface-raised)] p-4 shadow-2xl" onMouseDown={(e) => e.stopPropagation()}>
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-[14px] font-semibold text-[color:var(--text-primary)]">
            {isNew ? 'Save as prompt' : 'Save new version'}
          </h2>
          <button type="button" onClick={onClose} className="text-[color:var(--text-muted)] hover:text-[color:var(--text-primary)]"><X size={16} /></button>
        </div>

        {isNew ? (
          <label className="mb-3 block">
            <span className={labelCls}>Prompt name</span>
            <input autoFocus value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. refund-agent" className={inputCls} />
          </label>
        ) : (
          <div className="mb-3 text-[11.5px] text-[color:var(--text-muted)]">
            Saving a new version of <span className="font-medium text-[color:var(--text-secondary)]">{existingName}</span>.
          </div>
        )}

        {/* GitHub-style line diff of the loaded version → what's being saved. */}
        <div className="mb-3">
          <span className={labelCls}>{baselineHash ? `Changes since ${baselineHash}` : 'New prompt · full contents'}</span>
          <DiffView diff={diff} />
        </div>

        <label className="block">
          <span className={labelCls}>Commit message</span>
          <input value={commitMessage} onChange={(e) => setCommitMessage(e.target.value)} placeholder="what changed…" className={inputCls} />
        </label>

        <div className="mt-3">
          <span className={labelCls}>Apply a label (optional)</span>
          {!addingLabel ? (
            <BrandSelect
              value={chosenLabel}
              minWidth={0}
              leadingIcon={<Tag size={12} className="text-[color:var(--text-muted)]" />}
              options={[
                { value: '', label: 'No label' },
                ...teamLabels.map((l) => ({ value: l, label: l, icon: <Tag size={12} className="text-[color:var(--text-muted)]" /> })),
                { value: '__new__', label: 'Create new label…', icon: <Plus size={12} className="text-[color:var(--trodo-primary)]" /> },
              ]}
              onChange={(v) => {
                setConfirmMove(false);
                if (v === '__new__') { setAddingLabel(true); setNewLabel(''); } else setChosenLabel(v);
              }}
            />
          ) : (
            <div className="flex items-center gap-2">
              <input autoFocus value={newLabel} onChange={(e) => setNewLabel(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') confirmAddLabel(); if (e.key === 'Escape') setAddingLabel(false); }}
                placeholder="e.g. canary" className={`${inputCls} flex-1`} />
              <button type="button" onClick={confirmAddLabel} className="rounded-md bg-[color:var(--trodo-primary)] px-2.5 py-1.5 text-[12px] font-medium text-white hover:opacity-90">Add</button>
              <button type="button" onClick={() => setAddingLabel(false)} className="rounded-md border border-[color:var(--border-default)] px-2.5 py-1.5 text-[12px] text-[color:var(--text-muted)]">Cancel</button>
            </div>
          )}
          {labelMoves && !confirmMove && (
            <div className="mt-1.5 text-[11px] text-[color:var(--text-muted)]">
              <span className="font-medium">{chosenLabel}</span> currently points at <span className="font-mono">{shortHash(labelHolder.version_hash)}</span>. Saving moves it here.
            </div>
          )}
        </div>

        {error && <div className="mt-3 rounded-lg border border-red-300 bg-red-50 px-2.5 py-2 text-[11.5px] text-red-700">{error}</div>}

        <div className="mt-4 flex justify-end gap-2">
          {confirmMove ? (
            <>
              <button type="button" onClick={() => setConfirmMove(false)} className="rounded-md border border-[color:var(--border-default)] px-3 py-1.5 text-[12.5px] text-[color:var(--text-muted)] hover:bg-surface-hover">Back</button>
              <button type="button" onClick={doSave} disabled={saving}
                className="inline-flex items-center gap-1.5 rounded-md bg-[color:var(--trodo-primary)] px-3 py-1.5 text-[12.5px] font-medium text-white hover:opacity-90 disabled:opacity-50">
                {saving ? <Loader2 size={13} className="animate-spin" /> : <Tag size={13} />} Confirm and push to {chosenLabel}
              </button>
            </>
          ) : (
            <>
              <button type="button" onClick={onClose} className="rounded-md border border-[color:var(--border-default)] px-3 py-1.5 text-[12.5px] text-[color:var(--text-muted)] hover:bg-surface-hover">Cancel</button>
              <button type="button"
                onClick={() => { if (labelMoves) setConfirmMove(true); else doSave(); }}
                disabled={saving || addingLabel}
                className="inline-flex items-center gap-1.5 rounded-md bg-[color:var(--trodo-primary)] px-3 py-1.5 text-[12.5px] font-medium text-white hover:opacity-90 disabled:opacity-50">
                {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
                {chosenLabel ? `Save and push to ${chosenLabel}` : (isNew ? 'Save prompt' : 'Save new version')}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
